import { useTheme, Tooltip } from "@mui/material";
import React, { useCallback } from "react";

import { Box, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";

import { useFormContext, useWatch, Controller } from "react-hook-form";
import { DayOfWeekEnum } from "../../../../../API";
import { AlertHoursPicker } from "../../../../../common/components/datePicker/AlertHoursPicker";
import { WeekdayPills } from "../../../../../common/components/datePicker/WeekdayPills";
import SimplePaperWrapper from "../../../../../common/components/item/SimplePaperWrapper";
import StatusToggle from "../../../../../common/components/item/StatusToggle";
import { ModelSettingsParamFormValues } from "./AlertModelSettings";
import { successNotification } from "../../../../../common/variables/notification";
import confirmDialogStyleOptions from "../../../../../common/helpers/confirmDialogParams";
import StyledLoadingButton from "../../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { ServiceTypeEnum } from "../../../../model-manager/variables/modelManager";

type RowProps = {
  index: number;
  isDisabled: boolean;
  meta: {
    Icon: React.ElementType;
    label: string;
    desc: string;
  };
  tooltip: React.ReactNode;
  onSubmitAll: () => Promise<void> | void;
};

const ModelSettingsParamRow = React.memo(function ModelSettingsParamRow({
  index,
  meta,
  tooltip,
  isDisabled,
  onSubmitAll,
}: RowProps) {
  const theme = useTheme();

  const confirm = useConfirm();

  const { control, setValue } = useFormContext<ModelSettingsParamFormValues>();

  const isEditing = useWatch({ control, name: `parameters.${index}.editMode` });

  const onSaveLoading = useWatch({
    control,
    name: `parameters.${index}.onSaveLoading`,
  });

  const onToggleLoading = useWatch({
    control,
    name: `parameters.${index}.onToggleLoading`,
  });

  const activeDays = useWatch({
    control,
    name: `parameters.${index}.activeDays`,
  });

  const Icon = meta.Icon;

  const onToggle = useCallback(async (next: boolean) => {
    const disableOrEnable = next ? "Enable" : "Disable";

    const stopOrStart = next ? "start" : "stop";

    confirm({
      title: `${disableOrEnable} ${meta.label} object type?`,
      description: `You’re about to ${disableOrEnable.toLowerCase()} this object type. The system will ${stopOrStart} detecting and generating alerts for it.`,
      confirmationText: disableOrEnable,
      cancellationText: "Cancel",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        color: "primary",
      },
    })
      .then(async () => {
        setValue(`parameters.${index}.onToggleLoading`, true, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });

        setValue(`parameters.${index}.enabled`, next);

        await onSubmitAll();

        setValue(`parameters.${index}.onToggleLoading`, false, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });

        setValue(`parameters.${index}.editMode`, false);

        const activatedOrDeactivated = next ? "activated" : "deactivated";

        successNotification(
          `${meta.label} type ${activatedOrDeactivated} successfully`
        );
      })
      .catch(() => {
        setValue(`parameters.${index}.onToggleLoading`, false, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });

        setValue(`parameters.${index}.enabled`, !next);
      });
  }, []);

  const onSaveHandler = useCallback(async () => {
    if (isEditing) {
      try {
        setValue(`parameters.${index}.onSaveLoading`, true, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });

        await onSubmitAll();

        setValue(`parameters.${index}.onSaveLoading`, false, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });

        setValue(`parameters.${index}.editMode`, false, {
          shouldDirty: false,
        });
      } catch (error) {
        setValue(`parameters.${index}.onSaveLoading`, false, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    } else {
      setValue(`parameters.${index}.editMode`, true, {
        shouldDirty: true,
      });
    }
  }, [isEditing]);

  return (
    <SimplePaperWrapper
      sx={{
        "&.MuiPaper-root": {
          boxShadow: "none",
          display: "inline-block",
          height: "100%",
          borderRadius: "8px",
          border: t => `1px solid ${t.palette.otherOutlineBorder.main}`,
        },
        "& .MuiSwitch-root": { margin: 0 },
        maxWidth: "100%",
        width: "100%",
        mb: 1.5,
      }}
    >
      <Box sx={{ p: "0.75em" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "1em",
            justifyContent: "space-between",
            mb: "0.75em",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.5em",
            }}
          >
            {Icon && <Icon aria-label={meta.label} />}

            <Typography variant="body2Regular">{meta.label}</Typography>
          </Box>

          <Tooltip title={tooltip} placement="top-start" arrow>
            <Box>
              <Controller
                name={`parameters.${index}.enabled`}
                render={({ field }) => (
                  <StatusToggle
                    loading={!!onToggleLoading}
                    checked={!!field.value}
                    formControlLabelStyles={{ margin: 0 }}
                    handleChange={() => onToggle(!field.value)}
                  />
                )}
              />
            </Box>
          </Tooltip>
        </Box>

        <Typography
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 400,
            display: "block",
          }}
          variant="body1Regular"
        >
          {meta.desc}
        </Typography>
      </Box>

      {meta.label !== ServiceTypeEnum.HARD_HAT && (
        <Box sx={{ borderTop: `1px solid ${theme.palette.otherDivider.main}` }}>
          <Box
            sx={{
              p: "0.75em",
              display: "flex",
              flexDirection: "column",
              gap: "0.75em",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: "0.5em",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Controller
                name={`parameters.${index}.activeDays`}
                render={({ field }) => (
                  <WeekdayPills
                    value={(field.value ?? []) as DayOfWeekEnum[]}
                    onChange={field.onChange}
                    disabled={!isEditing}
                  />
                )}
              />

              <StyledLoadingButton
                variant="outlined"
                loading={onSaveLoading}
                className={isDisabled ? "Mui-disabled-custom" : ""}
                sx={{ height: "2rem", width: "4rem", fontSize: "0.875rem" }}
                type="button"
                onClick={onSaveHandler}
              >
                {isEditing ? "Save" : "Edit"}
              </StyledLoadingButton>
            </Box>
          </Box>

          {isEditing && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                borderTop: `1px solid ${theme.palette.otherDivider.main}`,
              }}
            >
              <Box sx={{ p: "0.75em" }}>
                <Controller
                  name={`parameters.${index}.schedules`}
                  render={({ field }) => (
                    <AlertHoursPicker
                      value={field.value ?? []}
                      activeDays={activeDays}
                      onChange={field.onChange}
                      label="Alert hours"
                      stepMinutes={30}
                    />
                  )}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </SimplePaperWrapper>
  );
});

export default ModelSettingsParamRow;
